// src/components/management/ManagementHub.jsx
import React, { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";

// Import existing management components
import UserList from "../UserList";
import SchoolClassManagement from "./SchoolClassManagement";
import SubjectManagement from "./SubjectManagement";
import DeletedSemesterReviewsPage from "../semesterreview/DeletedSemesterReviewsPage";

function ManagementHub() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("schools");

  const isAdmin = user?.roles?.some((role) => role.name === "ADMIN");
  const isBerufsbildner = user?.roles?.some((role) => role.name === "BERUFSBILDNER");
  const isPracticeInstructor = user?.roles?.some((role) => 
    role.name === "PRACTICE_INSTRUCTOR" || role.name === "PRAXISBILDNER"
  );

  // Set default tab for Practice Instructor if they access this page
  // This must remain before any early returns to satisfy React Hook rules
  React.useEffect(() => {
    if (isPracticeInstructor && !isAdmin && !isBerufsbildner && activeTab !== "users") {
        setActiveTab("users");
    }
  }, [isPracticeInstructor, isAdmin, isBerufsbildner, activeTab]);

  if (!isAdmin && !isBerufsbildner && !isPracticeInstructor) {
    return (
      <div className="min-h-[60vh] bg-sand-50 grid place-items-center px-4">
        <div className="card max-w-lg w-full text-center">
          <div className="w-16 h-16 rounded-full bg-gc-100 text-gc-800 grid place-items-center mx-auto mb-4">
            <i className="fas fa-exclamation-triangle text-xl" />
          </div>
          <h2 className="text-xl font-semibold text-ink mb-1">Access denied</h2>
          <p className="text-ink-muted">
            This feature is available for administrators and instructors only.
          </p>
        </div>
      </div>
    );
  }

  const tabs = [
    ...(isAdmin || isBerufsbildner ? [
      { id: "schools", label: "Schools & Classes", icon: "fa-school" },
      { id: "subjects", label: "Subject Management", icon: "fa-book-open" },
    ] : []),
    ...(isAdmin || isPracticeInstructor ? [{ id: "users", label: "User Management", icon: "fa-users-cog" }] : []),
    ...(isAdmin || isBerufsbildner ? [{ id: "trash", label: "Deleted", icon: "fa-trash-alt" }] : []),
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case "schools":
        return <SchoolClassManagement />;
      case "subjects":
        return <SubjectManagement />;
      case "users":
        return <UserList />;
      case "trash":
        return <DeletedSemesterReviewsPage />;
      default:
        // Safe fallback for restricted roles
        return <SchoolClassManagement />;
    }
  };

  return (
    <div className="min-h-screen bg-green-cloud">
      {/* Header */}
      <header className="hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="">
            <div className="card-brand-elevated p-6 bg-gradient-to-r from-ice-white to-pale-dawn/30">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-grey-dusk flex items-center mb-2">
                    <i className="fas fa-users-cog text-sunrise-red mr-4"></i>
                    Management Hub
                  </h1>
                  <p className="text-slate-cloud mt-2">
                    Centralized management for users, schools, classes, and
                    subjects
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Body */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <section className="bg-ice-white rounded-2xl shadow-soft border border-pale-dawn/60 overflow-hidden">
          {/* Tabs */}
          <div className="border-b border-pale-dawn/70 bg-pale-dawn/30 px-2 md:px-4">
            <nav className="flex flex-wrap gap-2 md:gap-3 py-3">
              {tabs.map((tab) => {
                const active = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={[
                      "inline-flex items-center rounded-xl px-4 py-2 text-sm font-medium transition-all",
                      active
                        ? "bg-sunrise-red text-ice-white shadow-soft"
                        : "bg-ice-white border border-pale-dawn text-grey-dusk hover:border-sunrise-red/30 hover:bg-pale-dawn/30",
                    ].join(" ")}
                  >
                    <i
                      className={`fas ${tab.icon} mr-2 ${
                        active ? "opacity-95" : "text-slate-cloud"
                      }`}
                    />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Content */}
          <div className="p-4 md:p-6 bg-ice-white">{renderTabContent()}</div>
        </section>
      </main>
    </div>
  );
}

export default ManagementHub;

/* 
 Utility classes expected from your theme setup:
 - .card { @apply bg-white border border-gc-200 rounded-2xl p-6 shadow-soft; }
 - .hero { background: linear-gradient(180deg, #99B2A7 0%, #E9E1D1 100%); }
*/
